/**
 * When a failed send is worth trying again, and how.
 *
 * Deliberately **zero imports**. The retry rule is the part worth testing on
 * its own, and anything reaching into `@/api/client` pulls `react-native` with
 * it, which the root `node --test` suites cannot load — the same reason
 * `launch-refresh.ts` exists. `idempotent-send.ts` is the thin wrapper that
 * supplies the key.
 *
 * Errors are matched by `name` rather than `instanceof` for the same reason.
 * `api/client.ts` sets each of those names explicitly, and
 * `tests/inbox/../send-idempotency.test.ts` asserts it still does, so a rename
 * fails a test rather than silently turning every retry off.
 */

/** Shape of the errors this has to classify, without importing their classes. */
type ErrorLike = { name?: unknown; status?: unknown };

/**
 * Might the message have landed despite this failure?
 *
 * Only transport failures qualify:
 *
 *   TimeoutError   we gave up waiting. The server may well have committed.
 *   5xx            the server broke, possibly after storing the message.
 *   TypeError      `fetch` never got a response at all — nothing is known.
 *
 * A 4xx is the server having read the request and refused it: the conversation
 * ended, the group was left, the body was rejected. Repeating that fails again,
 * more slowly, and a 401 in particular is already being handled by the session
 * layer. So they are rethrown untouched and immediately.
 */
export function isTransientSendFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { name, status } = error as ErrorLike;
  if (name === "TimeoutError") return true;
  if (typeof status === "number") return status >= 500;
  return name === "TypeError";
}

/**
 * Run `attempt` with one stable key, retrying once on a transport failure.
 *
 * The key belongs to the message, not to the attempt: both tries get the same
 * one, which is the entire reason the second try is safe. Generating a fresh
 * key on retry would post twice, which is the bug this exists to prevent.
 *
 * One retry, not a backoff loop — somebody is waiting on this, and a send that
 * has failed twice should say so rather than spin.
 */
export async function withOneRetry<T>(
  attempt: (clientMessageId: string) => Promise<T>,
  clientMessageId: string,
  sleep: (ms: number) => Promise<void>,
  delayMs = 400,
): Promise<T> {
  try {
    return await attempt(clientMessageId);
  } catch (error) {
    if (!isTransientSendFailure(error)) throw error;
    await sleep(delayMs);
    return attempt(clientMessageId);
  }
}
